import { useState } from "react";
import { Box, Flex, Grid, Spinner } from "@chakra-ui/react";
import { Building2, ReceiptText, TrendingUp } from "lucide-react";

import {
  KpiCard,
  Panel,
  palette,
} from "@/containers/app/summary/components/ui";
import YearMonthPicker from "@/components/YearMonthPicker";
import { currentYearMonth } from "@/helpers/period";
import { money } from "@/hooks/useNumberFormatter";
import { usePortalContext } from "@/hooks/usePortalContext";
import { usePortalContexts } from "@/hooks/usePortalContexts";

import CommonLayout from "../CommonLayout";
import { OrgCollectionTable } from "./components/OrgCollectionTable";
import { OrgQueryError } from "./components/OrgQueryError";
import { useOrgAnalytics } from "./hooks/useOrgAnalytics";

const OrgAnalyticsContainer = (): JSX.Element => {
  const [period, setPeriod] = useState<string>(currentYearMonth);
  const { context } = usePortalContext();
  const { orgs } = usePortalContexts();
  const query = useOrgAnalytics(period);

  const orgName =
    query.data?.organization.displayName ??
    orgs.find((org) => org.id === context?.id)?.displayName ??
    "";

  const total = query.data?.total;
  const merchants = query.data?.merchants ?? [];
  const collecting = merchants.filter((m) => m.collected > 0).length;

  const kpis = [
    {
      title: "Recaudo del mes",
      value: money(total?.collected ?? 0),
      badge: `${merchants.length} comercios`,
      color: palette.blue,
      icon: TrendingUp,
    },
    {
      title: "Facturas pagadas",
      value: (total?.paidInvoices ?? 0).toLocaleString("es-CO"),
      badge: period,
      color: palette.green,
      icon: ReceiptText,
    },
    {
      title: "Comercios",
      value: String(merchants.length),
      badge: `${collecting} con recaudo`,
      color: palette.purple,
      icon: Building2,
    },
  ];

  return (
    <CommonLayout
      title="Recaudo consolidado"
      subtitle={orgName}
      sticky
      buttons={
        <Box w="140px">
          <YearMonthPicker
            value={period}
            setValue={(next) => {
              if (next) setPeriod(next);
            }}
            // An empty period would query for nothing, so the month stays put.
            isClearable={false}
            placeholder="Periodo"
          />
        </Box>
      }
      table={
        <Box pb={8}>
          <Grid
            templateColumns={{ base: "1fr", xl: `repeat(${kpis.length}, 1fr)` }}
            gap={4}
            mb={4}
          >
            {kpis.map((kpi) => (
              <KpiCard key={kpi.title} {...kpi} />
            ))}
          </Grid>

          <Panel
            title="Recaudo por comercio"
            subtitle="Lo recaudado en el periodo por cada comercio de la organización."
          >
            {query.isError ? (
              <OrgQueryError
                message="el recaudo consolidado"
                code={query.error.data?.code}
                onRetry={() => void query.refetch()}
              />
            ) : query.isLoading ? (
              <Flex justify="center" align="center" py={10}>
                <Spinner size="md" color={palette.purple} />
              </Flex>
            ) : (
              <OrgCollectionTable merchants={merchants} />
            )}
          </Panel>
        </Box>
      }
    />
  );
};

export default OrgAnalyticsContainer;
